import { jsx, jsxs } from 'react/jsx-runtime';
import { Button } from './Button.js';
import '../ui/alert.js';
import '@radix-ui/react-avatar';
import 'date-fns-tz';
import { cn } from '../../utils/formatters.js';
import '@radix-ui/react-label';
import '../ui/button.js';
import '@radix-ui/react-progress';
import '@clerk/clerk-react';
import { Search, SortAsc, SortDesc, MoreVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { useState, useCallback } from 'react';
import '../../lib/toast.js';
import 'react-router-dom';
import { Skeleton } from '../ui/skeleton.js';
import '../../contexts/AppContext.js';
import '../../contexts/OrganizationContext.js';
import 'axios';
import '@tanstack/react-query';
import './FormPageLayout.js';
import 'cmdk';
import '@radix-ui/react-dialog';
import '@radix-ui/react-popover';
import '../form/RichTextEditor.js';
import '@radix-ui/react-switch';
import '../crud/CrudDetailView.js';
import '../crud/CrudFormView.js';
import '../../contexts/SidebarContext.js';
import '../../contexts/ThemeContext.js';
import '@dnd-kit/core';
import '@dnd-kit/sortable';
import '@dnd-kit/utilities';
import { Badge } from '../ui/badge.js';
import 'react-day-picker';
import '../ui/chart.js';
import '@radix-ui/react-checkbox';
import '@radix-ui/react-collapsible';
import { Input } from '../ui/input.js';
import 'vaul';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuLabel, DropdownMenuItem } from '../ui/dropdown-menu.js';
import '../ui/radio-group.js';
import '@radix-ui/react-select';
import '../ui/time-input.js';
import '@radix-ui/react-separator';
import '../ui/sidebar.js';
import 'react-toastify';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '../ui/table.js';
import '@radix-ui/react-tabs';
import '@radix-ui/react-tooltip';

/** Cell text alignment per column `align` value. */
const CELL_ALIGN_CLASSES = {
    left: undefined,
    right: 'text-right',
    center: 'text-center',
};
/** Header flex justification per column `align` value (header content is a flex row). */
const HEADER_ALIGN_CLASSES = {
    left: undefined,
    right: 'justify-end',
    center: 'justify-center',
};
/**
 * Reusable data table component with sorting, pagination, and search
 * @param columns - Column definitions for the table
 * @param data - Array of data to display
 * @param loading - Whether data is loading
 * @param pagination - Pagination information
 * @param searchable - Whether to show search input
 * @param searchPlaceholder - Placeholder text for search input
 * @param emptyMessage - Message to show when no data
 * @param emptyState - Empty state component to render when no data (overrides emptyMessage)
 * @param onRowClick - Callback when row is clicked
 * @param onSearch - Callback when search query changes
 * @param onSort - Callback when column header is clicked for sorting
 * @param onPageChange - Callback when page is changed
 * @param currentSort - Current sort state
 * @param actions - Row actions to display
 * @param className - Additional CSS classes
 */
function DataTable({ columns, data, loading = false, pagination, searchable = false, searchPlaceholder = 'Search...', emptyMessage = 'No data found', emptyState, onRowClick, onSearch, onSort, onPageChange, currentSort, actions, className = '', skeletonRows = 10, toolbarLeft, toolbarRight, belowToolbar, }) {
    const [searchQuery, setSearchQuery] = useState('');
    /**
     * Handle search input changes
     */
    const handleSearchChange = useCallback((value) => {
        setSearchQuery(value);
        onSearch?.(value);
    }, [onSearch]);
    /**
     * Handle column header click for sorting
     */
    const handleSort = useCallback((field) => {
        const column = columns.find(col => col.key === field);
        if (!column?.sortable)
            return;
        const newDirection = currentSort?.field === field && currentSort.direction === 'asc' ? 'desc' : 'asc';
        onSort?.(field, newDirection);
    }, [columns, currentSort, onSort]);
    /**
     * Format cell value based on column configuration
     */
    const formatCellValue = useCallback((item, column) => {
        const value = item[column.key];
        // Use custom formatter if provided
        if (column.format) {
            return column.format(value, item);
        }
        // Default formatting based on column type
        switch (column.type) {
            case 'boolean':
                return value ? jsx(Badge, { variant: "secondary", children: "Yes" }) : jsx(Badge, { variant: "outline", children: "No" });
            case 'date':
                return value ? new Date(value).toLocaleDateString() : '-';
            case 'datetime':
                return value ? new Date(value).toLocaleString() : '-';
            case 'decimal':
            case 'number':
                return typeof value === 'number' ? value.toLocaleString() : value;
            case 'tags':
                if (Array.isArray(value)) {
                    return (jsxs("div", { className: "flex flex-wrap gap-1", children: [value.slice(0, 3).map((tag, idx) => (jsx(Badge, { variant: "outline", className: "text-xs", children: tag }, `${item.id || idx}-tag-${idx}`))), value.length > 3 && (jsxs(Badge, { variant: "secondary", className: "text-xs", children: ["+", value.length - 3] }, `${item.id || 'more'}-tag-more`))] }));
                }
                return value;
            case 'status':
                if (value === 'ACTIVE') {
                    return jsx(Badge, { variant: "default", children: "Active" });
                }
                else if (value === 'INACTIVE') {
                    return jsx(Badge, { variant: "secondary", children: "Inactive" });
                }
                else if (value === 'DISCONTINUED') {
                    return jsx(Badge, { variant: "destructive", children: "Discontinued" });
                }
                return jsx(Badge, { variant: "outline", children: value });
            default:
                return value?.toString() || '-';
        }
    }, []);
    const totalPages = pagination?.totalPages || 1;
    const currentPage = pagination?.page || 1;
    return (jsxs("div", { className: `space-y-4 ${className}`, children: [searchable && (jsxs("div", { className: "flex items-center gap-4", children: [toolbarLeft, jsxs("div", { className: "relative flex-1", children: [jsx(Search, { className: "absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" }), jsx(Input, { placeholder: searchPlaceholder, value: searchQuery, onChange: e => handleSearchChange(e.target.value), className: "pl-10" })] }), toolbarRight] })), belowToolbar, data.length === 0 && !loading ? (
            // Empty state - render outside table for better visibility
            jsx("div", { className: "rounded-xl border bg-card py-12 px-6 text-center", children: emptyState ? (
                // Render custom empty state component if provided
                jsx("div", { className: "w-full", children: emptyState })) : (
                // Fallback to simple text message
                jsxs("div", { className: "text-muted-foreground", children: [emptyMessage, searchQuery && (jsx("div", { className: "mt-1", children: jsx(Button, { variant: "link", onClick: () => handleSearchChange(''), className: "p-0 h-auto", children: "Clear search" }) }))] })) })) : (jsxs("div", { className: "rounded-xl border bg-card", children: [jsxs(Table, { children: [jsx(TableHeader, { children: jsxs(TableRow, { children: [columns.map(column => {
                                            const isSortable = column.sortable;
                                            const isSorted = currentSort?.field === column.key;
                                            return (jsx(TableHead, { className: cn(isSortable && 'cursor-pointer hover:bg-muted/50 select-none', column.hideOnMobile && 'hidden md:table-cell'), onClick: () => isSortable && handleSort(column.key), children: jsxs("div", { className: cn('flex items-center gap-2', HEADER_ALIGN_CLASSES[column.align ?? 'left']), children: [column.label, isSortable && (jsx("div", { className: "flex flex-col", children: isSorted && currentSort?.direction === 'asc' ? (jsx(SortAsc, { className: "h-3 w-3" })) : isSorted && currentSort?.direction === 'desc' ? (jsx(SortDesc, { className: "h-3 w-3" })) : (jsx("div", { className: "h-3 w-3 opacity-30", children: jsx(SortAsc, { className: "h-3 w-3" }) })) }))] }) }, column.key));
                                        }), actions && actions.length > 0 && (jsx(TableHead, { className: "w-[100px] hidden md:table-cell text-right", children: "Actions" }))] }) }), jsx(TableBody, { children: loading
                                    ? // Loading skeleton - vary widths for more natural appearance
                                        Array.from({ length: skeletonRows }).map((_, i) => (jsxs(TableRow, { children: [columns.map((column, colIndex) => {
                                                    // Vary skeleton widths based on column index for more natural look
                                                    const widthVariants = ['w-full', 'w-[90%]', 'w-[95%]', 'w-[85%]'];
                                                    const widthClass = widthVariants[(i + colIndex) % widthVariants.length];
                                                    return (jsx(TableCell, { className: cn(column.hideOnMobile && 'hidden md:table-cell'), children: jsx(Skeleton, { className: `h-4 ${widthClass}` }) }, column.key));
                                                }), actions && actions.length > 0 && (jsx(TableCell, { className: "hidden md:table-cell", children: jsx(Skeleton, { className: "h-6 w-16" }) }))] }, i)))
                                    : // Data rows
                                        data.map((item, index) => (jsxs(TableRow, { className: onRowClick ? 'cursor-pointer hover:bg-muted/50' : '', onClick: e => {
                                                // Don't trigger row click when clicking on actions menu or clickable cells
                                                if (e.target.closest('[role="button"]') ||
                                                    e.target.closest('[data-clickable-cell]')) {
                                                    return;
                                                }
                                                onRowClick?.(item, e);
                                            }, children: [columns.map(column => (jsx(TableCell, { className: cn(column.hideOnMobile && 'hidden md:table-cell', CELL_ALIGN_CLASSES[column.align ?? 'left']), "data-clickable-cell": column.disableRowClick ? 'true' : undefined, onClick: e => {
                                                        if (column.disableRowClick) {
                                                            e.stopPropagation();
                                                        }
                                                    }, children: formatCellValue(item, column) }, column.key))), actions && actions.length > 0 && (jsx(TableCell, { className: "hidden md:table-cell text-right", onClick: e => e.stopPropagation(), children: jsxs(DropdownMenu, { children: [jsx(DropdownMenuTrigger, { asChild: true, children: jsxs(Button, { variant: "ghost", className: "h-8 w-8 p-0", children: [jsx("span", { className: "sr-only", children: "Open menu" }), jsx(MoreVertical, {})] }) }), jsxs(DropdownMenuContent, { align: "end", children: [jsx(DropdownMenuLabel, { children: "Actions" }), actions.map(action => (jsx(DropdownMenuItem, { onClick: e => {
                                                                            // Support Cmd/Ctrl+click for actions that have a URL
                                                                            if ((e.metaKey || e.ctrlKey) && action.getUrl) {
                                                                                e.preventDefault();
                                                                                const url = action.getUrl(item);
                                                                                window.open(url, '_blank');
                                                                            }
                                                                            else {
                                                                                action.onClick(item, e);
                                                                            }
                                                                        }, children: action.label }, action.key)))] })] }) }))] }, item.id || index))) })] }), pagination && totalPages > 1 && (jsxs("div", { className: "flex flex-col md:flex-row md:items-center gap-3 px-6 py-4 border-t", children: [jsxs("div", { className: "flex gap-2 w-full md:w-auto order-1 md:order-3", children: [jsx(Button, { variant: "outline", size: "sm", onClick: () => onPageChange?.(currentPage - 1), disabled: !pagination.hasPrev, icon: ChevronLeft, className: "flex-1 md:flex-initial", children: "Previous" }), jsx(Button, { variant: "outline", size: "sm", onClick: () => onPageChange?.(currentPage + 1), disabled: !pagination.hasNext, icon: ChevronRight, iconPosition: "right", className: "flex-1 md:flex-initial", children: "Next" })] }), jsxs("div", { className: "text-sm text-muted-foreground text-center md:text-left order-2 md:order-1 md:flex-1", children: ["Showing ", (currentPage - 1) * pagination.limit + 1, " to", ' ', Math.min(currentPage * pagination.limit, pagination.total), " of ", pagination.total, ' ', "results"] }), jsxs("span", { className: "text-sm w-full text-center md:w-auto order-3 md:order-2", children: ["Page ", currentPage, " of ", totalPages] })] }))] }))] }));
}

export { DataTable };
//# sourceMappingURL=DataTable.js.map
